import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Button, Combobox, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Divider, Field, Input, MessageBar, MessageBarBody, Option } from "@fluentui/react-components";
import { DismissRegular, Eye20Regular, EyeOff20Regular, PersonRegular } from "@fluentui/react-icons";
import { User, UserCreateDTO, UserUpdateDTO } from "../../models/users";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import apiClient from "../../services/api";
import { createUser, getUserByEmail, resetPasswordRequest, updateUser } from "../../services/usersService";
import { useAppContext } from "../../AppContext";
import { RoleDTO } from "../../models/role";
import { createUserRoleAssignment, getRolesByType } from "../../services/roleService";
import { Module } from "../../models/module";

type Props = {
  onClose: () => void;
  open: boolean;
  onUserUpdated: (updatedUser: User) => void;
};

const CreateUserDialog = (props: Props) => {
  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm<any>({
    defaultValues: {
      Email: "",
      FirstName: "",
      LastName: "",
      Title: "",
      PhoneNumber: "",
      Password: "",
      ConfirmPassword: "",
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [assignExistingUser, setAssignExistingUser] = useState<{ email: string; moduleID: number; moduleName: string; roleID: number; roleName: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [roles, setRoles] = useState<RoleDTO[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [selectedModuleIds, setSelectedModuleIds] = useState<number[]>([]);


  const {user, featurePermissions} = useAppContext();

  const isForestandardeUser = user && user.roles.some((r: RoleDTO) => r.roleName === "Föreståndare");

  const password = watch("Password");

  useEffect(() => {
    if (props.open) {
      reset({
        Email: "",
        FirstName: "",
        LastName: "",
        Title: "",
        PhoneNumber: "",
        Password: "",
        ConfirmPassword: "",
      });
      setSelectedRoleId(null);
      setSelectedModuleIds([]);
      setErrorMessage(null);
      setAssignExistingUser(null);
    }
    const getRoles = async () => {
      try{
        const response = await getRolesByType("Supplier");
        setRoles(response.filter((r: RoleDTO) => r.roleName !== "Föreståndare"));
      }
      catch(error){
        console.error("Error fetching roles:", error);
      }
    };

    if(user && user.roles.some((r: RoleDTO) => r.roleName === "Föreståndare")){
      const userModules = user.assignments.filter((a: any) => a.role.roleName === "Föreståndare").map((a: any) => a.module);
      setModules(userModules);
      // Pre-select if only one module
      if (userModules.length === 1) {
        setSelectedModuleIds([userModules[0].moduleID]);
      }
      getRoles();
    }

  }, [props.open, reset]);

  const onSubmit = async (formData: any) => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const createdUser: UserCreateDTO = {
      Email: formData.Email.trim(),
      FirstName: formData.FirstName,
      LastName: formData.LastName,
      PhoneNumber: formData.PhoneNumber,
      Title: formData.Title,
      Password: formData.Password,
      ...(isForestandardeUser && selectedRoleId && { RoleID: selectedRoleId }),
      ...(isForestandardeUser && selectedModuleIds.length > 0 && { Modules: selectedModuleIds })
    };

      const response = await createUser(createdUser);
      
      // Send password reset request to the new user only if no password was provided
      if (!formData.Password) {
        await resetPasswordRequest(formData.Email.trim(), 1);
      }
      
      props.onUserUpdated(response);
      props.onClose();
    } catch (error: any) {
      console.error('Error creating user:', error);
      if (error?.response?.status === 409 && isForestandardeUser && selectedRoleId && selectedModuleIds.length > 0) {
        const moduleID = selectedModuleIds[0];
        const moduleName = modules.find(m => m.moduleID === moduleID)?.moduleName ?? '';
        const roleName = roles.find(r => r.roleID === selectedRoleId)?.roleName ?? '';
        setAssignExistingUser({ email: formData.Email.trim(), moduleID, moduleName, roleID: selectedRoleId, roleName });
      } else if (error?.response?.status === 409) {
        setErrorMessage('En användare med denna e-postadress finns redan.');
      } else if (error?.response?.status === 403) {
        setErrorMessage('E-postadressen måste tillhöra din organisation.');
      } else {
        setErrorMessage('Ett fel uppstod. Kontrollera uppgifterna och försök igen.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssignExistingUser = async () => {
    if (!assignExistingUser) return;
    setIsLoading(true);
    try {
      const existingUser = await getUserByEmail(assignExistingUser.email);
      await createUserRoleAssignment(existingUser.userID, assignExistingUser.roleID, assignExistingUser.moduleID);
      props.onUserUpdated(existingUser);
      setAssignExistingUser(null);
      props.onClose();
    } catch (error) {
      console.error('Error assigning existing user:', error);
      setAssignExistingUser(null);
      setErrorMessage('Ett fel uppstod vid tilldelning av användaren.');
    } finally {
      setIsLoading(false);
    }
  };

  return(
    <>
    <Dialog
      open={props.open}
      onOpenChange={(_event, data) => !data.open && props.onClose()}
      modalType="alert"
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle 
          action={
            <Button
              appearance="subtle"
              shape="circular"
              aria-label="close"
              icon={<DismissRegular />}
              onClick={props.onClose}
            />
          }>
            Inställningar
          </DialogTitle>
          <DialogContent>
            {errorMessage && (
              <MessageBar intent="error" className="mb-3">
                <MessageBarBody>{errorMessage}</MessageBarBody>
              </MessageBar>
            )}
            <Field label="E-post:" required validationMessage={errors.Email?.message as string}>
              <Input 
                {...register("Email", {
                  required: "E-post är obligatorisk",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Ogiltig e-postadress"
                  }
                })}
              />
            </Field>
            <Field label="Förnamn: " validationMessage={errors.FirstName?.message as string}>
              <Input
                {...register("FirstName")}
              />
            </Field>
            <Field label="Efternamn: " validationMessage={errors.LastName?.message as string}>
              <Input 
                {...register("LastName")}
              />
            </Field>
            <Field label="Titel: " validationMessage={errors.Title?.message as string}>
              <Input
                {...register("Title")}
              />
            </Field>
            <Field label="Telefon: " validationMessage={errors.PhoneNumber?.message as string}>
              <Input
                {...register("PhoneNumber")}
              />
            </Field>
            {isForestandardeUser && (
              <>
                <Field label="Roll:">
                  <Combobox
                    placeholder="Välj roll"
                    onOptionSelect={(_e, data) => {
                      const roleId = data.optionValue ? parseInt(data.optionValue) : null;
                      setSelectedRoleId(roleId);
                    }}
                  >
                    {roles.map((role) => (
                      <Option key={role.roleID} value={role.roleID.toString()}>
                        {role.roleName}
                      </Option>
                    ))}
                  </Combobox>
                </Field>
                <Field label="Verksamhet:">
                  <Combobox
                    placeholder="Välj verksamhet"
                    disabled={modules.length === 1}
                    value={modules.find(m => selectedModuleIds.includes(m.moduleID))?.moduleName ?? ''}
                    selectedOptions={selectedModuleIds.map(id => id.toString())}
                    onOptionSelect={(_e, data) => {
                      const moduleId = data.optionValue ? parseInt(data.optionValue) : null;
                      if (moduleId) {
                        setSelectedModuleIds([moduleId]);
                      }
                    }}
                  >
                    {modules.map((module) => (
                      <Option key={module.moduleID} value={module.moduleID.toString()}>
                        {module.moduleName}
                      </Option>
                    ))}
                  </Combobox>
                </Field>
              </>
            )}
            <Divider className="mt-3 mb-2" />
            <Field label="Lösenord:" validationMessage={errors.Password?.message as string}>
              <Input 
                type={"password"}
                {...register("Password", {
                  minLength: {
                    value: 6,
                    message: "Lösenordet måste vara minst 6 tecken"
                  }
                })}
              />
            </Field>
            <Field label="Bekräfta lösenord:" validationMessage={errors.ConfirmPassword?.message as string}>
              <Input 
                type={"password"}
                {...register("ConfirmPassword", {
                  validate: (value) => {
                    if (password && value !== password) {
                      return "Lösenorden matchar inte";
                    }
                    return true;
                  }
                })}
              />
            </Field>
          </DialogContent>
          <DialogActions>
            <Button onClick={props.onClose} disabled={isLoading}>Avbryt</Button>
            <Button 
              appearance="primary" 
              onClick={handleSubmit(onSubmit)}
              disabled={isLoading}
            >
              {isLoading ? "Sparar..." : "Spara"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>

    <Dialog open={!!assignExistingUser} modalType="alert">
      <DialogSurface>
        <DialogBody>
          <DialogContent>
            {assignExistingUser && (
              <div className="flex flex-col gap-4 py-1">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center rounded-full bg-blue-100 text-blue-600" style={{ width: 40, height: 40, flexShrink: 0 }}>
                    <PersonRegular fontSize={20} />
                  </div>
                  <div>
                    <div className="font-semibold text-base">Användaren finns redan</div>
                    <div className="text-sm" style={{ color: '#888' }}>Den här personen har redan ett konto</div>
                  </div>
                </div>

                <div className="rounded-lg" style={{ border: '1px solid #e0e0e0' }}>
                  <div className="flex items-center gap-2 px-4 py-2" style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#888', minWidth: 100 }}>E-post</span>
                    <span className="text-sm font-medium">{assignExistingUser.email}</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2" style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#888', minWidth: 100 }}>Organisation</span>
                    <span className="text-sm font-bold">{assignExistingUser.moduleName}</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2">
                    <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#888', minWidth: 100 }}>Roll</span>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ background: '#dbeafe', color: '#1d4ed8' }}>{assignExistingUser.roleName}</span>
                  </div>
                </div>

                <p className="text-sm" style={{ color: '#555' }}>
                  Vill du lägga till den här användaren till organisationen med ovanstående roll?
                </p>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssignExistingUser(null)} disabled={isLoading}>Avbryt</Button>
            <Button appearance="primary" onClick={handleAssignExistingUser} disabled={isLoading}>
              {isLoading ? "Sparar..." : "Ja, lägg till"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  </>
  )
}

export default CreateUserDialog;